import React, { useCallback } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * Format phone number input according to mask pattern
 * Mask format uses '#' for digits (e.g., "(###) ###-####")
 * 
 * @param {string} value - The raw input value
 * @param {string} mask - The format mask pattern
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (value, mask) => {
  if (!value || !mask) return value;
  
  // Extract only digits from the input
  const digitsOnly = value.replace(/\D/g, '');
  
  // If no digits, return empty string
  if (!digitsOnly) return '';
  
  // Apply the mask pattern
  let formatted = '';
  let digitIndex = 0;
  
  for (let i = 0; i < mask.length && digitIndex < digitsOnly.length; i++) {
    if (mask[i] === '#') {
      formatted += digitsOnly[digitIndex];
      digitIndex++;
    } else {
      formatted += mask[i];
    }
  }
  
  return formatted;
};

/**
 * PhoneNumberField component for Formik Field wrapper
 *
 * Features:
 * - Formats phone number according to tenant's phoneNumberFormat setting
 * - Only allows digits to be entered (other characters are automatically stripped)
 * - Country code display in input prefix
 * - Clean integration with Material-UI and Formik
 * - Server handles E.164 conversion for database storage
 * - Client receives national format numbers from server
 *
 * @param {Object} field - Formik field props
 * @param {Object} form - Formik form props
 * @param {string} mask - Phone format mask (e.g., "(###) ###-####")
 * @param {string} countryCode - Country code for display
 * @param {Object} props - Additional TextField props
 */
const PhoneNumberField = ({ field, form, mask, countryCode, ...props }) => {
  const { name, value } = field;
  const { setFieldValue } = form;

  /**
   * Change handler that formats the phone number according to the mask
   */
  const handleChange = useCallback((event) => {
    const formattedValue = formatPhoneNumber(event.target.value, mask);
    setFieldValue(name, formattedValue);
  }, [setFieldValue, name, mask]);

  return (
    <TextField
      {...props}
      name={name}
      value={value || ''}
      onChange={handleChange}
      inputProps={{
        ...props.inputProps,
        maxLength: mask ? mask.length : undefined,
      }}
      InputProps={{
        startAdornment: <InputAdornment position="start">{countryCode}</InputAdornment>,
        ...props.InputProps,
      }}
    />
  );
};

PhoneNumberField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  mask: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
};

PhoneNumberField.defaultProps = {
  countryCode: null,
  InputProps: {},
  inputProps: {},
};

/**
 * SimplePhoneNumberField component for direct useFormik hook usage
 * Use this when you have direct access to formik.setFieldValue
 *
 * @param {Object} formik - useFormik hook result
 * @param {string} fieldName - Name of the field in formik values
 * @param {string} mask - Phone format mask (e.g., "(###) ###-####")
 * @param {string} countryCode - Country code for display
 * @param {Object} props - Additional TextField props
 */
export const SimplePhoneNumberField = ({ formik, fieldName, mask, countryCode, ...props }) => {
  const handleChange = useCallback((event) => {
    const formattedValue = formatPhoneNumber(event.target.value, mask);
    formik.setFieldValue(fieldName, formattedValue);
  }, [formik, fieldName, mask]);

  return (
    <TextField
      {...props}
      name={fieldName}
      value={formik.values[fieldName] || ''}
      onChange={handleChange}
      inputProps={{
        ...props.inputProps,
        maxLength: mask ? mask.length : undefined,
      }}
      InputProps={{
        startAdornment: <InputAdornment position="start">{countryCode}</InputAdornment>,
        ...props.InputProps,
      }}
    />
  );
};

SimplePhoneNumberField.propTypes = {
  formik: PropTypes.shape({
    values: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    touched: PropTypes.object,
    errors: PropTypes.object,
  }).isRequired,
  fieldName: PropTypes.string.isRequired,
  mask: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
};

SimplePhoneNumberField.defaultProps = {
  countryCode: null,
  InputProps: {},
  inputProps: {},
};

export default PhoneNumberField;
